import { Calendar, UserPlus } from 'lucide-react';
import { FrequencyStatusEnum, type SearchFrequencyViewModel } from '@generated';
import { formatToShortDateAndTimeOrNull } from '~/util/date';
import type { DataTableColumnProps } from '~/shared/data-tables/DataTableColumn';
import { TableActions } from '~/shared/data-tables/TableActions';
import { createQueryDataTable, type PropsOfQueryDataTable, type RowOfQueryDataTable } from '~/shared/data-tables/query/QueryDataTable';
import { PatientAvatar } from '~/features/intake/patients/PatientAvatar';
import { DisciplineChip } from '~/libraries/disciplines/DisciplineChip';

const NeedsAssignedDataTable = createQueryDataTable('frequency');
type RowType = RowOfQueryDataTable<typeof NeedsAssignedDataTable>;
type TableProps = PropsOfQueryDataTable<typeof NeedsAssignedDataTable>;

function ActionsCell({ row }: { row: SearchFrequencyViewModel }) {
	return (
		<TableActions>
			<TableActions.Button aria-label="Assign" size="sm" Icon={UserPlus} />
			{row.invariants.canBeRescheduled && <TableActions.Button aria-label="Reschedule" size="sm" Icon={Calendar} />}
		</TableActions>
	);
}

const columnDefinitions: DataTableColumnProps<SearchFrequencyViewModel>[] = [
	{
		header: 'Patient',
		key: 'patientName',
		sortable: true,
		renderCell: row => (
			<PatientAvatar
				{...row}
				firstName={row.patientFirstName}
				lastName={row.patientLastName}
				middleInitial={row.patientMiddleInitial}
				visitAddress={row.visitAddressAddressOne}
			/>
		)
	},
	{
		header: 'Visit Type',
		key: 'frequencyLabel',
		sortable: true,
		renderCell: row => <DisciplineChip disciplineId={row.disciplineId} label={row.frequencyLabel} />
	},
	{
		header: 'Frequency',
		key: 'frequencyCadenceTypeLabel'
	},
	{
		header: 'Scheduled Date & Time',
		key: 'scheduledDateAndTime',
		renderCell: row => (
			<div className="flex items-center gap-2">
				<Calendar className="size-4 text-gray-400" />
				{formatToShortDateAndTimeOrNull(row.scheduledDateAndTime, '-')}
			</div>
		)
	},
	{
		header: 'Actions',
		key: 'actions',
		width: 'w-[120px]',
		renderCell: row => <ActionsCell row={row} />
	}
];

export function NeedsAssignedTable(props: TableProps) {
	return (
		<NeedsAssignedDataTable.Table
			columns={columnDefinitions}
			emptyMessage="No visits requiring assignment."
			staticFilters={[{ name: 'frequencyStatusId', operator: '==', value: FrequencyStatusEnum.NeedsStaffed }]}
			{...props}
		/>
	);
}
